import { RouteLocationRaw, Router } from 'vue-router';

import { LOG_PREFIX } from '@/types/enums';
import { AlertWrapper } from '@/types/responseTypes';

import { mapCtaUrlToRoute } from './ctaUrlMapper';

type AlertCta =
  | {
    text: string
    to: RouteLocationRaw
  }
  | {
    text: string
    href: string
  };

const DEFAULT_ALERT_CTA: AlertCta = {
  text: 'Check my report',
  to: { name: 'CreditReportSummary' },
};

const getAlertLogPrefix = (alertType: AlertWrapper['alertType']) =>
  `[${LOG_PREFIX.ALERTS}]: Alert rule ${alertType}`;

const logMissingAlertCtaFields = (
  alertType: AlertWrapper['alertType'],
  ctaButtonText: string | null | undefined,
  ctaButtonUrl: string | null | undefined,
) => {
  const missingFields = [
    ctaButtonText == null ? 'ctaButtonText' : null,
    ctaButtonUrl == null ? 'ctaButtonUrl' : null,
  ].filter(Boolean).join(', ');

  window.$log?.warn(`${getAlertLogPrefix(alertType)} is missing CTA field(s): ${missingFields}. Falling back to default CTA.`);
};

const logUnmappableAlertCtaUrl = (alertType: AlertWrapper['alertType'], ctaButtonUrl: string) => {
  window.$log?.warn(
    `${getAlertLogPrefix(alertType)} has an unmappable ctaButtonUrl "${ctaButtonUrl}". Falling back to the supplied URL.`,
  );
};

export const resolveAlertCta = (
  alert: AlertWrapper | undefined,
  router: Pick<Router, 'resolve'>,
): AlertCta => {
  if (!alert) {
    return DEFAULT_ALERT_CTA;
  }

  const hasCtaButtonText = alert.ctaButtonText != null;
  const hasCtaButtonUrl = alert.ctaButtonUrl != null;

  // We need to check if both are null as this is intentional on the contentful side
  if (!hasCtaButtonText && !hasCtaButtonUrl) {
    return DEFAULT_ALERT_CTA;
  }

  // If only one is null then we assume there is a misconfiguration in contentful and log it
  if (!hasCtaButtonText || !hasCtaButtonUrl) {
    logMissingAlertCtaFields(alert.alertType, alert.ctaButtonText, alert.ctaButtonUrl);
    return DEFAULT_ALERT_CTA;
  }

  const ctaButtonText = alert.ctaButtonText!;
  const ctaButtonUrl = alert.ctaButtonUrl!;
  const mappedRoute = mapCtaUrlToRoute(ctaButtonUrl, router);

  if (!mappedRoute) {
    logUnmappableAlertCtaUrl(alert.alertType, ctaButtonUrl);
    return {
      text: ctaButtonText,
      href: ctaButtonUrl,
    };
  }

  return {
    text: ctaButtonText,
    to: mappedRoute,
  };
};
